import { UntypedFormGroup } from '@angular/forms';
import { DestroyRef } from '@angular/core';
import { debounceTime, finalize, fromEvent, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';

export function getDefaultReportDate(fallbackDate: string): string {
  return environment.temporaryReportDateEnabled ? environment.temporaryReportDate : fallbackDate;
}

export function getReportDatePreferenceKey(dateMode: 'AD' | 'BS'): 'AD' | 'BS' {
  return dateMode;
}

export function normalizeReportShowValues(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export async function resolveReportDateSelection(
  adDate: string,
  showBsDate: boolean,
  convertDate: (date: string) => Promise<ReportDateConversionLike | null>,
  formatDate: (date: string) => string,
  fallbackDate: string,
): Promise<{ requestValue: string; displayLabel: string }> {
  if (!adDate) {
    return { requestValue: fallbackDate, displayLabel: fallbackDate };
  }

  if (showBsDate) {
    try {
      const conversion = await convertDate(adDate);
      if (conversion) {
        return {
          requestValue: conversion.selected_date || conversion.bs_date,
          displayLabel: conversion.selected_date_label || conversion.bs_date_label,
        };
      }
    } catch {
      // Fall through to the AD date when conversion is unavailable.
    }
  }

  return { requestValue: adDate, displayLabel: formatDate(adDate) };
}

export interface ReportBlockCellLike {
  value?: number | null;
  source?: string | null;
}

export interface ReportRowLike {
  row_key?: string | null;
  has_children?: boolean | null;
  force_expand_children?: boolean | null;
  row_kind?: string | null;
  station_id?: number | null;
  parent_row_key?: string | null;
  row_level?: number | null;
  installed_capacity?: number | null;
}

export interface ReportBlockValueLike {
  value?: number | null;
}

export interface ReportBlockRowLike {
  blocks: ReportBlockValueLike[];
}

export interface ReportHierarchyNodeLike<T extends ReportRowLike> {
  row: T;
  children: ReportHierarchyNodeLike<T>[];
}

export function sortReportGeneratorNodesByInstalledCapacity<T extends ReportRowLike>(
  nodes: ReportHierarchyNodeLike<T>[],
): ReportHierarchyNodeLike<T>[] {
  return [...nodes].sort((left, right) => {
    if (left.row.row_kind !== 'data' || right.row.row_kind !== 'data') {
      return 0;
    }

    return (right.row.installed_capacity ?? 0) - (left.row.installed_capacity ?? 0);
  });
}

function sortReportGeneratorRowsByInstalledCapacity<T extends ReportRowLike>(rows: T[]): T[] {
  return [...rows].sort((left, right) => (right.installed_capacity ?? 0) - (left.installed_capacity ?? 0));
}

export interface ReportGroupedHierarchyRowLike extends ReportRowLike {
  owner?: string | null;
  source_category?: string | null;
  sub_category?: string | null;
}

interface ReportHierarchySubGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  rows: T[];
}

interface ReportHierarchySourceGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  subGroups: Map<string, ReportHierarchySubGroupLike<T>>;
  directRows: T[];
}

interface ReportHierarchySectorGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  sourceGroups: Map<string, ReportHierarchySourceGroupLike<T>>;
  footerRows: T[];
}

interface ReportHierarchySourceChildren<T extends ReportGroupedHierarchyRowLike> {
  children: ReportHierarchyNodeLike<T>[];
  directLeafNodes: ReportHierarchyNodeLike<T>[];
  hasFuelGroups: boolean;
  subNodes: ReportHierarchyNodeLike<T>[];
}

export interface BuildGroupedReportHierarchyConfig<T extends ReportGroupedHierarchyRowLike> {
  rows: T[];
  isSummaryView: boolean;
  normalizeHierarchyLabel: (value: string | null | undefined) => string;
  normalizeOptionalHierarchyLabel: (value: string | null | undefined) => string | null;
  isFuelHierarchyLabel: (value: string | null | undefined) => boolean;
  isMissingHierarchyLabel: (value: string | null | undefined) => boolean;
  sortNodes: (nodes: ReportHierarchyNodeLike<T>[]) => ReportHierarchyNodeLike<T>[];
  buildAggregateRow: (
    rowKey: string,
    label: string,
    rows: T[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren?: boolean,
  ) => T;
}

export interface ReportDateConversionLike {
  data_date: string;
  bs_date: string;
  bs_date_label: string;
  selected_date: string;
  selected_date_label: string;
}

export interface ReportBsCalendarCellLike {
  bs_date: string;
  bs_date_label: string;
  ad_date: string;
  in_current_month: boolean;
  is_today: boolean;
  is_selected: boolean;
}

export interface ReportBsCalendarLike {
  data_date: string;
  bs_date: string;
  bs_year: number;
  bs_month: number;
  bs_month_name: string;
  selected_date: string;
  selected_date_label: string;
  cells: ReportBsCalendarCellLike[];
}

export function stripTrailingCapacityLabel(value: string | null | undefined): string {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  const openingParenthesis = trimmedValue.lastIndexOf('(');
  if (openingParenthesis < 0 || !trimmedValue.endsWith(')')) {
    return trimmedValue;
  }

  return trimmedValue.slice(0, openingParenthesis).trim();
}

export class ReportDataParsingBase {
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected selectedDateRequestValue = getDefaultReportDate('');
  protected selectedDateAdValue = getDefaultReportDate('');
  protected selectedDateDisplayLabel = getDefaultReportDate('');

  protected applyTemporaryReportDate(form: UntypedFormGroup, fallbackDate: string): void {
    const temporaryDate = getDefaultReportDate(fallbackDate);
    if (temporaryDate === fallbackDate) {
      return;
    }
    form.patchValue({ date: temporaryDate }, { emitEvent: false });
    this.selectedDateRequestValue = temporaryDate;
    this.selectedDateAdValue = temporaryDate;
    this.selectedDateDisplayLabel = temporaryDate;
  }

  protected extractResponseValue(response: unknown): Record<string, unknown> | null {
    return extractReportResponseValue(response);
  }

  protected getArrayValue(value: unknown, key: string): unknown[] {
    return getReportArrayValue(value, key);
  }

  protected getRecordValue(value: unknown, key: string): unknown {
    return getReportRecordValue(value, key);
  }

  protected getRecord(value: unknown): Record<string, unknown> {
    return getReportRecord(value);
  }

  protected extractStringValue(value: unknown, key: string): string | null {
    return extractReportStringValue(value, key);
  }

  protected extractBooleanValue(value: unknown, key: string): boolean | null {
    const candidate = this.getRecordValue(value, key);
    if (typeof candidate === 'boolean') {
      return candidate;
    }
    if (typeof candidate !== 'string') {
      return null;
    }

    const normalized = candidate.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
    return null;
  }

  protected normalizeNumber(value: unknown): number | null {
    return normalizeReportNumber(value);
  }

  protected normalizeNullableNumber(value: unknown): number | null {
    return this.normalizeNumber(value);
  }

  protected normalizeCellValue(value: string): number | null {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null;
  }

  protected handleMenuToggle(): void {
    const state = toggleReportSidebar(this.sidebarCollapsed, this.mobileSidebarOpen);
    this.sidebarCollapsed = state.sidebarCollapsed;
    this.mobileSidebarOpen = state.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }
}

export function resetReportEditForm(form: UntypedFormGroup): void {
  form.reset(
    { data_value: '', copy_previous_block_value: false, carry_over_previous_day_values: false, remarks: '' },
    { emitEvent: false },
  );
}

export interface ReportSummaryValues {
  scheduled_mwh: number;
  actual_mwh: number;
  deviation_mwh: number;
  row_count: number;
}

export function extractReportSummary(
  value: unknown,
  getRecordValue: (value: unknown, key: string) => unknown,
  getRecord: (value: unknown) => Record<string, unknown>,
  normalizeNumber: (value: unknown) => number | null,
): ReportSummaryValues {
  const record = getRecord(getRecordValue(value, 'summary'));
  return {
    scheduled_mwh: normalizeNumber(record['scheduled_mwh']) ?? 0,
    actual_mwh: normalizeNumber(record['actual_mwh']) ?? 0,
    deviation_mwh: normalizeNumber(record['deviation_mwh']) ?? 0,
    row_count: normalizeNumber(record['row_count']) ?? 0,
  };
}

export interface ReportGeneratorOptionValues {
  id: number;
  name: string;
  station_code: string | null;
  generator_name: string;
  project_unique_code: string | null;
  scada_station_code: string | null;
  other_parameter: string | null;
}

export function extractReportGeneratorOptions(
  response: unknown,
  extractResponse: (response: unknown) => Record<string, unknown> | null,
  getArray: (value: unknown, key: string) => unknown[],
  getRecord: (value: unknown) => Record<string, unknown>,
  extractString: (value: unknown, key: string) => string | null,
  normalizeNumber: (value: unknown) => number | null,
): ReportGeneratorOptionValues[] {
  const value = extractResponse(response);
  return getArray(value, 'generators').map((item) => {
    const record = getRecord(item);
    const name = extractString(record, 'generator_name') ?? extractString(record, 'name') ?? '-';
    const stationCode = extractString(record, 'scada_station_code') ?? extractString(record, 'station_code');
    return {
      id: normalizeNumber(record['id']) ?? 0,
      name,
      station_code: stationCode,
      generator_name: name,
      project_unique_code: extractString(record, 'project_unique_code'),
      scada_station_code: stationCode,
      other_parameter: extractString(record, 'other_parameter'),
    };
  });
}

export function extractReportBlockDefinitions<TPeriod extends string, T>(
  value: unknown,
  getArray: (value: unknown, key: string) => unknown[],
  getRecord: (value: unknown) => Record<string, unknown>,
  normalizeNumber: (value: unknown) => number | null,
  extractString: (value: unknown, key: string) => string | null,
  resolvePeriod: (period: string | null, label: string | null) => TPeriod,
  mapBlock: (record: Record<string, unknown>, block: { block_no: number; label: string; period: TPeriod }) => T,
): T[] {
  return getArray(value, 'blocks').map((item) => {
    const record = getRecord(item);
    const label = extractString(record, 'label') ?? '';
    const block = {
      block_no: normalizeNumber(record['block_no']) ?? 0,
      label,
      period: resolvePeriod(extractString(record, 'period'), label),
    };
    return mapBlock(record, block);
  });
}

export function isMissingReportHierarchyLabel(value: string | null | undefined): boolean {
  const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return candidate === '' || candidate === 'na' || candidate === 'n/a' || candidate === 'null' || candidate === '-';
}

export function normalizeReportHierarchyLabel(value: string | null | undefined, normalizeNea = false): string {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (normalizeNea && candidate.toUpperCase() === 'NEA SUBSIDIARY') {
    return 'NEA Subsidiary';
  }
  return candidate || 'Unassigned';
}

export function normalizeOptionalReportHierarchyLabel(value: string | null | undefined): string | null {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return candidate && !isMissingReportHierarchyLabel(candidate) ? candidate : null;
}

export function isFuelReportHierarchyLabel(value: string | null | undefined): boolean {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return Boolean(candidate) && !isMissingReportHierarchyLabel(candidate);
}

export function resolveReportBlockPeriod<TPeriod extends string>(
  period: string | null | undefined,
  label: string | null | undefined,
  resolveFromLabel: (label: string) => TPeriod,
): TPeriod {
  const normalizedPeriod = typeof period === 'string' ? period.trim().toLowerCase() : '';
  if (normalizedPeriod === 'morning' || normalizedPeriod === 'day' || normalizedPeriod === 'night') {
    return normalizedPeriod as TPeriod;
  }
  if (typeof label === 'string' && label.trim()) {
    return resolveFromLabel(label.trim());
  }
  return 'morning' as TPeriod;
}

export interface ReportPageInitializationConfig {
  applyTemporaryDefaultDate: () => void;
  bindFilterListeners: () => void;
  loadOptions?: () => void;
  listenToSocketEvents: () => void;
  listenToPreferenceChanges: () => void;
  handleDateSelectionChange: () => Promise<void>;
}

export async function initializeReportPage(config: ReportPageInitializationConfig): Promise<void> {
  config.applyTemporaryDefaultDate();
  config.bindFilterListeners();
  config.loadOptions?.();
  config.listenToSocketEvents();
  config.listenToPreferenceChanges();
  await config.handleDateSelectionChange();
}

export function toggleReportSidebar(
  sidebarCollapsed: boolean,
  mobileSidebarOpen: boolean,
): { sidebarCollapsed: boolean; mobileSidebarOpen: boolean } {
  if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
    return { sidebarCollapsed: !sidebarCollapsed, mobileSidebarOpen };
  }
  return { sidebarCollapsed, mobileSidebarOpen: !mobileSidebarOpen };
}

export function bindReportFilterListeners(
  form: UntypedFormGroup,
  destroyRef: DestroyRef,
  filterControlNames: string[],
  onDateChange: () => void,
  onFilterChange: () => void,
  onCopyPreviousValueChange: () => void,
  onCarryOverPreviousDayValuesChange: () => void,
): void {
  form.get('date')?.valueChanges
    .pipe(debounceTime(150), takeUntilDestroyed(destroyRef))
    .subscribe(onDateChange);

  for (const controlName of filterControlNames) {
    form.get(controlName)?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(destroyRef))
      .subscribe(onFilterChange);
  }

  form.get('copy_previous_block_value')?.valueChanges
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(onCopyPreviousValueChange);

  form.get('carry_over_previous_day_values')?.valueChanges
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(onCarryOverPreviousDayValuesChange);
}

export function listenToReportPreferenceChanges(
  destroyRef: DestroyRef,
  onPreferenceChange: () => void,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe((event) => {
      if (event.detail?.name === 'showBsDate') {
        onPreferenceChange();
      }
    });
}

export interface ReportViewState {
  expandedRowKeys: string[];
  scrollTop: number;
  scrollLeft: number;
}

export function captureReportViewState(
  tableWrap: HTMLElement | null,
  expandedRowKeys: Set<string>,
): ReportViewState {
  return {
    expandedRowKeys: Array.from(expandedRowKeys),
    scrollTop: tableWrap?.scrollTop ?? 0,
    scrollLeft: tableWrap?.scrollLeft ?? 0,
  };
}

export function restoreReportViewState(
  snapshot: ReportViewState | null,
  restoreRows: (expandedRowKeys: Set<string>, snapshot: ReportViewState) => void,
  clearSnapshot: () => void,
  detectChanges: () => void,
): void {
  if (!snapshot) {
    return;
  }

  const restore = (): void => {
    restoreRows(new Set(snapshot.expandedRowKeys), snapshot);
    clearSnapshot();
    detectChanges();
  };
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
    return;
  }
  setTimeout(restore, 0);
}

export function isCurrentBsReportDateSelected(selectedDate: string, currentBsDate: string | null): boolean {
  return Boolean(currentBsDate && selectedDate === currentBsDate);
}

export function isCurrentAdReportDateSelected(selectedDate: string, todayDate: string): boolean {
  return selectedDate === todayDate;
}

export function handleReportEditDataValueInput(
  event: Event,
  clearControls: (controlNames: string[]) => void,
): void {
  const target = event.target as HTMLInputElement | null;
  if (hasTextValue(target?.value ?? '')) {
    clearControls(['copy_previous_block_value', 'carry_over_previous_day_values']);
  }
}

export function shouldCloseReportDatePicker(
  isOpen: boolean,
  event: MouseEvent,
  datePickerElement: HTMLElement | null,
): boolean {
  return isOpen && !isOutsideElement(event.target, datePickerElement);
}

export function openReportDatePicker(
  isBsDateMode: boolean,
  bsCalendarOpen: boolean,
  closeBsDatePicker: () => void,
  openBsDatePicker: () => void,
  hiddenDateInput: HTMLInputElement | null,
): void {
  if (isBsDateMode) {
    if (bsCalendarOpen) {
      closeBsDatePicker();
      return;
    }
    openBsDatePicker();
    return;
  }

  if (!hiddenDateInput) {
    return;
  }
  if (typeof hiddenDateInput.showPicker === 'function') {
    hiddenDateInput.showPicker();
    return;
  }
  hiddenDateInput.click();
}

export function listenToReportSocketEvents<TEvent, TSocketId>(
  events$: Observable<TEvent>,
  socketId$: Observable<TSocketId>,
  destroyRef: DestroyRef,
  onEvent: (event: TEvent) => void,
  onSocketId: (socketId: TSocketId) => void,
): void {
  events$.pipe(takeUntilDestroyed(destroyRef)).subscribe(onEvent);
  socketId$.pipe(takeUntilDestroyed(destroyRef)).subscribe(onSocketId);
}

export function loadReportGeneratorOptions(
  request$: Observable<unknown>,
  destroyRef: DestroyRef,
  onSuccess: (response: unknown) => void,
  onError: () => void,
): void {
  request$.pipe(takeUntilDestroyed(destroyRef)).subscribe({ next: onSuccess, error: onError });
}

export interface LoadReportBsCalendarConfig<TCalendar> {
  dateValue: string;
  requestCalendar: () => Promise<unknown>;
  extractCalendar: (response: unknown) => TCalendar | null;
  setLoading: (loading: boolean) => void;
  setCalendar: (calendar: TCalendar | null) => void;
  detectChanges: () => void;
}

export async function loadReportBsCalendar<TCalendar>(config: LoadReportBsCalendarConfig<TCalendar>): Promise<void> {
  if (!config.dateValue) {
    return;
  }

  config.setLoading(true);
  try {
    const calendar = config.extractCalendar(await config.requestCalendar());
    if (calendar) {
      config.setCalendar(calendar);
    }
  } catch {
    config.setCalendar(null);
  } finally {
    config.setLoading(false);
    config.detectChanges();
  }
}

export interface HandleReportDateSelectionConfig {
  adDate: string;
  showBsDate: boolean;
  convertDate: (date: string) => Promise<ReportDateConversionLike | null>;
  formatDate: (date: string) => string;
  fallbackDate: string;
  setAdDate: (date: string) => void;
  setSelection: (requestValue: string, displayLabel: string) => void;
  loadReport: (preserveViewState: boolean) => void;
  preserveViewState: boolean;
}

export async function handleReportDateSelection(config: HandleReportDateSelectionConfig): Promise<void> {
  config.setAdDate(config.adDate);
  const selection = await resolveReportDateSelection(
    config.adDate,
    config.showBsDate,
    config.convertDate,
    config.formatDate,
    config.fallbackDate,
  );
  config.setSelection(selection.requestValue, selection.displayLabel);
  config.loadReport(config.preserveViewState);
}

export function buildReportExportFileName(dateValue: string): string {
  return `log_sheet_report_${dateValue}.xlsx`;
}

export function getReportSelectedDateFormValue(form: UntypedFormGroup, fallbackDate: string): string {
  const value = form.get('date')?.value;
  return typeof value === 'string' && value ? value : fallbackDate;
}

export function getReportSelectedDateValue(
  selectedDateRequestValue: string,
  form: UntypedFormGroup,
  fallbackDate: string,
): string {
  return selectedDateRequestValue || getReportSelectedDateFormValue(form, fallbackDate);
}

export interface ReportEditableCellLike {
  block_no: number;
  value: number | null;
  remarks: string | null;
  source?: string | null;
}

export interface ReportEditableRowLike {
  station_id: number;
  blocks: ReportEditableCellLike[];
}

export interface UpdateReportCellConfig<TRow extends ReportEditableRowLike, TNode extends { row: TRow; children: TNode[] }> {
  hierarchyRoots: TNode[];
  cachedRows: TRow[];
  stationId: number;
  blockNo: number;
  blockValue: number | null;
  remarks: string | null;
  mapCell?: (cell: ReportEditableCellLike) => ReportEditableCellLike;
}

export function updateReportCellValue<TRow extends ReportEditableRowLike, TNode extends { row: TRow; children: TNode[] }>(
  config: UpdateReportCellConfig<TRow, TNode>,
): TRow[] {
  const updateRow = (row: TRow): void => {
    if (row.station_id !== config.stationId) {
      return;
    }
    row.blocks = row.blocks.map((cell) => {
      if (cell.block_no !== config.blockNo) {
        return cell;
      }
      const updatedCell = { ...cell, value: config.blockValue, remarks: config.remarks, source: 'logsheet_data_details' };
      return config.mapCell ? config.mapCell(updatedCell) : updatedCell;
    });
  };

  const traverse = (nodes: TNode[]): void => {
    for (const node of nodes) {
      updateRow(node.row);
      if (node.children.length) {
        traverse(node.children);
      }
    }
  };

  traverse(config.hierarchyRoots);
  return config.cachedRows.map((row) => {
    updateRow(row);
    return row;
  });
}

export interface SubmitReportEditConfig {
  request$: Observable<unknown>;
  destroyRef: DestroyRef;
  setSaving: (saving: boolean) => void;
  detectChanges: () => void;
  onSuccess: () => void;
  onError: () => void;
}

export function submitReportEdit(config: SubmitReportEditConfig): void {
  config.setSaving(true);
  config.request$
    .pipe(
      takeUntilDestroyed(config.destroyRef),
      finalize(() => {
        config.setSaving(false);
        config.detectChanges();
      }),
    )
    .subscribe({ next: config.onSuccess, error: config.onError });
}

export interface RefreshReportGeneratorOptionsConfig<TOption> {
  visibleOptions: TOption[];
  isDetailMode: boolean;
  selectedId: number | null;
  clearInvalidSelection: boolean;
  setFilteredOptions: (options: TOption[]) => void;
  getOptionId: (option: TOption) => number;
  getOptionName: (option: TOption) => string;
  clearSelection: () => void;
  setSearchTerm: (value: string) => void;
}

export function refreshReportGeneratorOptions<TOption>(config: RefreshReportGeneratorOptionsConfig<TOption>): void {
  config.setFilteredOptions([...config.visibleOptions]);
  if (!config.isDetailMode || config.selectedId === null) {
    return;
  }

  const selectedOption = config.visibleOptions.find((option) => config.getOptionId(option) === config.selectedId);
  if (selectedOption) {
    config.setSearchTerm(config.getOptionName(selectedOption));
    return;
  }

  if (config.clearInvalidSelection) {
    config.setSearchTerm('');
    config.clearSelection();
  }
}

export interface ReportPreviousValueLike {
  data_value?: number | null;
  remarks?: string | null;
}

export function patchReportPreviousValue(form: UntypedFormGroup, previous: ReportPreviousValueLike | null): void {
  if (previous?.data_value !== null && previous?.data_value !== undefined) {
    form.patchValue({ data_value: String(previous.data_value) }, { emitEvent: false });
  }
  if (previous?.remarks !== null && previous?.remarks !== undefined) {
    form.patchValue({ remarks: previous.remarks }, { emitEvent: false });
  }
}

export function cloneReportRowWithBlocks<TRow extends { blocks: TBlock[] }, TBlock extends object>(row: TRow): TRow {
  return {
    ...row,
    blocks: row.blocks.map((block) => ({ ...block })),
  } as TRow;
}

interface ReportAggregateInputRow {
  blocks: Array<{ block_no: number; value?: number | null }>;
  scheduled_mwh: number;
  actual_mwh: number;
  deviation_mwh: number;
  installed_capacity?: number | null;
  energy_mwh?: number | null;
}

interface ReportAggregateBlock<TPeriod extends string> {
  block_no: number;
  label: string;
  period: TPeriod;
  is_peak?: boolean;
}

export interface ReportAggregateData<TPeriod extends string> {
  blocks: Array<{ block_no: number; label: string; period: TPeriod; value: number }>;
  scheduled_mwh: number;
  actual_mwh: number;
  deviation_mwh: number;
  energy_mwh: number;
  installed_capacity: number;
}

export function aggregateReportRows<TRow extends ReportAggregateInputRow, TPeriod extends string>(
  rows: TRow[],
  blockColumns: ReportAggregateBlock<TPeriod>[],
): ReportAggregateData<TPeriod> {
  const blocks = blockColumns.map((block) => {
    const total = rows.reduce((sum, row) => {
      const cell = row.blocks.find((item) => item.block_no === block.block_no);
      return sum + (cell?.value ?? 0);
    }, 0);
    return {
      block_no: block.block_no,
      label: block.label,
      period: block.period,
      value: Number(total.toFixed(5)),
    };
  });

  return {
    blocks,
    scheduled_mwh: Number(rows.reduce((sum, row) => sum + row.scheduled_mwh, 0).toFixed(5)),
    actual_mwh: Number(rows.reduce((sum, row) => sum + row.actual_mwh, 0).toFixed(5)),
    deviation_mwh: Number(rows.reduce((sum, row) => sum + row.deviation_mwh, 0).toFixed(5)),
    energy_mwh: Number(rows.reduce((sum, row) => sum + (row.energy_mwh ?? 0), 0).toFixed(5)),
    installed_capacity: Number(rows.reduce((sum, row) => sum + (row.installed_capacity ?? 0), 0).toFixed(5)),
  };
}

export interface ReportDecodedRowValues<TBlock> {
  station_id: number;
  station_name: string;
  station_code: string | null;
  owner: string | null;
  source_category: string | null;
  sub_category: string | null;
  other_parameter: string | null;
  blocks: TBlock[];
  scheduled_mwh: number;
  actual_mwh: number;
  deviation_mwh: number;
  row_key: string;
  row_kind: string;
  row_level: number;
  has_children: boolean;
  force_expand_children: boolean;
  force_flat: boolean;
}

export interface ReportDecodedBlockValues<TPeriod extends string = string> {
  block_no: number;
  label: string;
  period: TPeriod;
  value: number | null;
  remarks: string | null;
  source: string | null;
  editable_id: number | null;
}

export interface ReportRowExtractionConfig<TPeriod extends string, TBlock, TRow> {
  getArray: (value: unknown, key: string) => unknown[],
  getRecord: (value: unknown) => Record<string, unknown>,
  normalizeNumber: (value: unknown) => number | null,
  extractString: (value: unknown, key: string) => string | null,
  resolvePeriod: (period: string | null, label: string | null) => TPeriod,
  mapBlock: (record: Record<string, unknown>, block: ReportDecodedBlockValues<TPeriod>) => TBlock,
  mapRow: (record: Record<string, unknown>, row: ReportDecodedRowValues<TBlock>) => TRow,
}

export function extractReportRows<TPeriod extends string, TBlock, TRow>(
  value: unknown,
  config: ReportRowExtractionConfig<TPeriod, TBlock, TRow>,
): TRow[] {
  return config.getArray(value, 'rows').map((item) => {
    const record = config.getRecord(item);
    const blocks = config.getArray(record, 'blocks').map((blockItem) => {
      const blockRecord = config.getRecord(blockItem);
      const label = config.extractString(blockRecord, 'label') ?? '';
      const block: ReportDecodedBlockValues<TPeriod> = {
        block_no: config.normalizeNumber(blockRecord['block_no']) ?? 0,
        label,
        period: config.resolvePeriod(config.extractString(blockRecord, 'period'), label),
        value: config.normalizeNumber(blockRecord['value']),
        remarks: config.extractString(blockRecord, 'remarks'),
        source: config.extractString(blockRecord, 'source'),
        editable_id: config.normalizeNumber(blockRecord['editable_id']),
      };
      return config.mapBlock(blockRecord, block);
    });
    const stationId = config.normalizeNumber(record['station_id']) ?? 0;
    const row: ReportDecodedRowValues<TBlock> = {
      station_id: stationId,
      station_name: config.extractString(record, 'station_name') ?? '-',
      station_code: config.extractString(record, 'station_code'),
      owner: config.extractString(record, 'owner'),
      source_category: config.extractString(record, 'source_category'),
      sub_category: config.extractString(record, 'sub_category'),
      other_parameter: config.extractString(record, 'other_parameter'),
      blocks,
      scheduled_mwh: config.normalizeNumber(record['scheduled_mwh']) ?? 0,
      actual_mwh: config.normalizeNumber(record['actual_mwh']) ?? 0,
      deviation_mwh: config.normalizeNumber(record['deviation_mwh']) ?? 0,
      row_key: config.extractString(record, 'row_key') ?? `data-${stationId}`,
      row_kind: config.extractString(record, 'row_kind') ?? 'data',
      row_level: config.normalizeNumber(record['row_level']) ?? 3,
      has_children: Boolean(record['has_children']),
      force_expand_children: Boolean(record['force_expand_children']),
      force_flat: Boolean(record['force_flat']),
    };
    return config.mapRow(record, row);
  });
}

export interface ReportOptionLike {
  id?: number | string | null;
  name?: string | null;
  station_code?: string | null;
}

export interface ReportBlockDefinitionLike {
  period: string;
}

export interface ReportBlockGroupLike<TPeriod extends string = string> {
  period: TPeriod;
  label: string;
  span: number;
}

export interface ReportHierarchyOrderRowLike {
  row_kind?: string | null;
  row_key?: string | null;
  station_name?: string | null;
}

export interface ReportAutocompleteSearchConfig<TOption extends ReportOptionLike> {
  query: unknown;
  form: UntypedFormGroup;
  controlName: string;
  normalizeNumber: NormalizeNumberFn;
  filterOptions: (query: string) => void;
  findOption: (label: string) => TOption | null;
}

export interface ReportAutocompleteCommitConfig<TOption extends ReportOptionLike> {
  searchTerm: unknown;
  form: UntypedFormGroup;
  controlName: string;
  normalizeNumber: NormalizeNumberFn;
  findOption: (label: string) => TOption | null;
  setSearchTerm: (value: string) => void;
}

export type NormalizeNumberFn = (value: unknown) => number | null;

export function formatReportNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '-';
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function getReportCellText(cell: ReportBlockCellLike): string {
  return cell.value === null || cell.value === undefined ? '_' : formatReportNumber(cell.value);
}

export function getReportDateLabel(displayLabel: string, requestValue: string, adValue: string): string {
  return displayLabel || requestValue || adValue;
}

export function buildGeneratorReportHeaderSummary(rowCount: number, blockCount: number, blockLabel: string): string {
  return `Showing ${rowCount} generator${rowCount === 1 ? '' : 's'} â€¢ ${blockCount} ${blockLabel}${blockCount === 1 ? '' : 's'}`;
}

export function getNullableNumberValue(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Number(value);
}

export function buildEditDataFormValue(value: number | null | undefined, remarks: string | null | undefined) {
  return {
    data_value: value === null || value === undefined ? '' : String(value),
    copy_previous_block_value: false,
    carry_over_previous_day_values: false,
    remarks: remarks ?? '',
  };
}

export function isLogsheetDataDetailCell(cell: ReportBlockCellLike): boolean {
  return cell.source === 'logsheet_data_details';
}

export function toggleReportHierarchyRow(row: ReportRowLike, expandedRowKeys: Set<string>): boolean {
  if (!row.row_key || !row.has_children) {
    return false;
  }

  if (expandedRowKeys.has(row.row_key)) {
    expandedRowKeys.delete(row.row_key);
  } else {
    expandedRowKeys.add(row.row_key);
  }

  return true;
}

export function buildLinkedReportHierarchyNodes<T extends ReportRowLike>(
  rows: T[],
  sortNodes: (nodes: ReportHierarchyNodeLike<T>[]) => ReportHierarchyNodeLike<T>[],
): ReportHierarchyNodeLike<T>[] {
  const nodeMap = new Map<string, ReportHierarchyNodeLike<T>>();
  const rootNodes: ReportHierarchyNodeLike<T>[] = [];

  for (const row of rows) {
    const rowKey = row.row_key ?? `${row.row_kind ?? 'data'}-${row.station_id ?? 0}`;
    nodeMap.set(rowKey, { row: { ...row, row_key: rowKey }, children: [] });
  }

  for (const node of nodeMap.values()) {
    const parentKey = node.row.parent_row_key;
    const parentNode = parentKey ? nodeMap.get(parentKey) : null;
    if (parentNode) {
      parentNode.children.push(node);
      continue;
    }

    rootNodes.push(node);
  }

  for (const node of nodeMap.values()) {
    if (node.children.length) {
      node.row.has_children = true;
      node.children = sortNodes(node.children);
    }
  }

  return rootNodes;
}

export function buildGroupedReportHierarchyNodes<T extends ReportGroupedHierarchyRowLike>(
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T>[] {
  const { sectorGroupMap, sectorLabels } = groupReportHierarchyRows(config);
  const sectorNodes: ReportHierarchyNodeLike<T>[] = [];

  for (const sectorLabel of sectorLabels) {
    const sectorGroup = sectorGroupMap.get(sectorLabel);
    if (!sectorGroup) {
      continue;
    }

    sectorNodes.push(...buildReportHierarchySectorRootNodes(sectorLabel, sectorGroup, config));
  }

  return config.sortNodes(sectorNodes);
}

function groupReportHierarchyRows<T extends ReportGroupedHierarchyRowLike>(
  config: BuildGroupedReportHierarchyConfig<T>,
): {
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>;
  sectorLabels: string[];
} {
  const sectorGroupMap = new Map<string, ReportHierarchySectorGroupLike<T>>();
  const sectorLabels: string[] = [];

  for (const row of config.rows) {
    addReportHierarchyRowToGroups(sectorGroupMap, sectorLabels, row, config);
  }

  return { sectorGroupMap, sectorLabels };
}

function addReportHierarchyRowToGroups<T extends ReportGroupedHierarchyRowLike>(
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>,
  sectorLabels: string[],
  row: T,
  config: BuildGroupedReportHierarchyConfig<T>,
): void {
  const sectorLabel = config.normalizeHierarchyLabel(row.owner);
  const sourceLabel = config.normalizeHierarchyLabel(row.source_category);
  const subLabel = config.normalizeOptionalHierarchyLabel(row.sub_category);
  const sectorGroup = getOrCreateReportHierarchySectorGroup(sectorGroupMap, sectorLabels, sectorLabel);
  if (row.row_kind === 'power-small' || row.parent_row_key) {
    sectorGroup.footerRows.push(row);
    return;
  }

  const sourceGroup = getOrCreateReportHierarchySourceGroup(sectorGroup, sourceLabel);

  if (subLabel) {
    const subGroup = getOrCreateReportHierarchySubGroup(sourceGroup, subLabel);
    subGroup.rows.push(row);
    return;
  }

  sourceGroup.directRows.push(row);
}

function getOrCreateReportHierarchySectorGroup<T extends ReportGroupedHierarchyRowLike>(
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>,
  sectorLabels: string[],
  sectorLabel: string,
): ReportHierarchySectorGroupLike<T> {
  const existingGroup = sectorGroupMap.get(sectorLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySectorGroupLike<T> = {
    label: sectorLabel,
    sourceGroups: new Map<string, ReportHierarchySourceGroupLike<T>>(),
    footerRows: [],
  };
  sectorGroupMap.set(sectorLabel, newGroup);
  sectorLabels.push(sectorLabel);
  return newGroup;
}

function getOrCreateReportHierarchySourceGroup<T extends ReportGroupedHierarchyRowLike>(
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  sourceLabel: string,
): ReportHierarchySourceGroupLike<T> {
  const existingGroup = sectorGroup.sourceGroups.get(sourceLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySourceGroupLike<T> = {
    label: sourceLabel,
    subGroups: new Map<string, ReportHierarchySubGroupLike<T>>(),
    directRows: [],
  };
  sectorGroup.sourceGroups.set(sourceLabel, newGroup);
  return newGroup;
}

function getOrCreateReportHierarchySubGroup<T extends ReportGroupedHierarchyRowLike>(
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  subLabel: string,
): ReportHierarchySubGroupLike<T> {
  const existingGroup = sourceGroup.subGroups.get(subLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySubGroupLike<T> = {
    label: subLabel,
    rows: [],
  };
  sourceGroup.subGroups.set(subLabel, newGroup);
  return newGroup;
}

function buildReportHierarchySectorRootNodes<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T>[] {
  if (shouldLiftReportFooterRows(sectorLabel, sectorGroup, config)) {
    return buildLinkedReportHierarchyNodes(sectorGroup.footerRows, config.sortNodes);
  }

  return [buildReportHierarchySectorNode(sectorLabel, sectorGroup, config)];
}

function shouldLiftReportFooterRows<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): boolean {
  const normalizedLabel = sectorLabel.trim().toLowerCase();
  return (
    (config.isMissingHierarchyLabel(sectorLabel) || normalizedLabel === 'unassigned') &&
    sectorGroup.sourceGroups.size === 0 &&
    sectorGroup.footerRows.length > 0
  );
}

function buildReportHierarchySectorNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const sourceNodes: ReportHierarchyNodeLike<T>[] = [];
  const sectorRows = [
    ...Array.from(sectorGroup.sourceGroups.values()).flatMap((group) => [
      ...group.directRows,
      ...Array.from(group.subGroups.values()).flatMap((subGroup) => subGroup.rows),
    ]),
    ...sectorGroup.footerRows.filter((row) => !row.parent_row_key && row.row_kind !== 'power-small'),
  ];

  for (const [sourceLabel, sourceGroup] of sectorGroup.sourceGroups.entries()) {
    sourceNodes.push(buildReportHierarchySourceNode(sectorLabel, sourceLabel, sourceGroup, config));
  }
  const footerNodes = buildLinkedReportHierarchyNodes(sectorGroup.footerRows, config.sortNodes);

  const sectorRow = config.buildAggregateRow(
    `sector-total-${slugifyReportLabel(sectorLabel)}`,
    `Total ${sectorLabel}`,
    sectorRows,
    0,
    'sector-total',
    true,
    config.isSummaryView,
  );

  return {
    row: sectorRow,
    children: [...sourceNodes, ...footerNodes],
  };
}

function buildReportHierarchySourceNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const { children, directLeafNodes, hasFuelGroups, subNodes } = buildReportHierarchySourceChildren(
    sectorLabel,
    sourceLabel,
    sourceGroup,
    config,
  );
  const sourceRows = [
    ...directLeafNodes.map((node) => node.row),
    ...subNodes.flatMap((node) => node.children.map((child) => child.row)),
  ];

  const sourceRow = config.buildAggregateRow(
    `source-total-${slugifyReportLabel(sectorLabel)}-${slugifyReportLabel(sourceLabel)}`,
    `Total ${sourceLabel}`,
    sourceRows,
    1,
    'source-total',
    hasFuelGroups || directLeafNodes.length > 0,
    config.isSummaryView && hasFuelGroups,
  );

  return {
    row: sourceRow,
    children,
  };
}

function buildReportHierarchySourceChildren<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchySourceChildren<T> {
  const subGroups = Array.from(sourceGroup.subGroups.entries());
  const fuelSubGroups = subGroups.filter(([, group]) => config.isFuelHierarchyLabel(group.label));
  const directLeafRows = sortReportGeneratorRowsByInstalledCapacity([
    ...sourceGroup.directRows,
    ...subGroups
      .filter(([, group]) => !config.isFuelHierarchyLabel(group.label))
      .flatMap(([, group]) => group.rows),
  ]);
  const subNodes = fuelSubGroups.map(([subLabel, subGroup]) =>
    buildReportHierarchySubNode(sectorLabel, sourceLabel, subLabel, subGroup, config),
  );
  const directLeafNodes = directLeafRows.map((leafRow) => ({
    row: leafRow,
    children: [],
  }));
  const hasFuelGroups = fuelSubGroups.length > 0;
  let children: ReportHierarchyNodeLike<T>[];

  if (config.isSummaryView) {
    children = hasFuelGroups ? subNodes : directLeafNodes;
  } else if (hasFuelGroups) {
    children = [...subNodes, ...directLeafNodes];
  } else {
    children = directLeafNodes;
  }

  return {
    children,
    directLeafNodes,
    hasFuelGroups,
    subNodes,
  };
}

function buildReportHierarchySubNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  subLabel: string,
  subGroup: ReportHierarchySubGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const subRow = config.buildAggregateRow(
    `subcat-total-${slugifyReportLabel(sectorLabel)}-${slugifyReportLabel(sourceLabel)}-${slugifyReportLabel(subLabel)}`,
    `Total ${subLabel}`,
    subGroup.rows,
    2,
    'subcat-total',
    true,
    false,
  );

  return {
    row: subRow,
    children: sortReportGeneratorRowsByInstalledCapacity(subGroup.rows).map((leafRow) => ({
      row: leafRow,
      children: [],
    })),
  };
}

export function flattenReportHierarchyNodes<T extends ReportRowLike>(
  nodes: ReportHierarchyNodeLike<T>[],
  expandedKeys: Set<string>,
  shouldForceExpand: (node: ReportHierarchyNodeLike<T>) => boolean = () => false,
): T[] {
  const rows: T[] = [];

  for (const node of nodes) {
    rows.push(node.row);
    const rowKey = node.row.row_key;
    if (node.row.has_children && rowKey && (shouldForceExpand(node) || expandedKeys.has(rowKey))) {
      rows.push(...flattenReportHierarchyNodes(node.children, expandedKeys, shouldForceExpand));
    }
  }

  return rows;
}

export function getReportRowBlockExtremeValue(row: ReportBlockRowLike, mode: 'max' | 'min'): number | null {
  const values = row.blocks
    .map((cell) => cell.value)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  if (!values.length) {
    return null;
  }

  const extremeValue = mode === 'max' ? Math.max(...values) : Math.min(...values);
  return Number(extremeValue.toFixed(5));
}

export function isOutsideElement(target: EventTarget | null, element: HTMLElement | null | undefined): boolean {
  return Boolean(target && element && !element.contains(target as Node));
}

export function hasTextValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function clearTruthyFormControls(form: UntypedFormGroup, controlNames: string[]): void {
  for (const controlName of controlNames) {
    const control = form.get(controlName);
    if (control?.value) {
      control.patchValue(false, { emitEvent: false });
    }
  }
}

export function applyPreviousValueMode(form: UntypedFormGroup, mode: 'copy' | 'carry'): void {
  form.patchValue(
    {
      copy_previous_block_value: mode === 'copy',
      carry_over_previous_day_values: mode === 'carry',
    },
    { emitEvent: true },
  );
}

export function shouldFetchPreviousValue(
  form: UntypedFormGroup,
  selectedControlName: string,
  resetControlName: string,
  hasContext: boolean,
  disabled = false,
): boolean {
  const selectedControl = form.get(selectedControlName);
  if (!selectedControl?.value || !hasContext || disabled) {
    return false;
  }

  form.patchValue({ [resetControlName]: false }, { emitEvent: false });
  return true;
}

export function normalizeReportSearchQuery(query: unknown): string {
  return typeof query === 'string' ? query.trim().toLowerCase() : '';
}

export function filterReportOptions<T extends ReportOptionLike>(options: T[], query: string): T[] {
  const normalizedQuery = normalizeReportSearchQuery(query);
  if (!normalizedQuery) {
    return [...options];
  }

  return options.filter((option) => {
    const name = (option.name ?? '').toLowerCase();
    const code = (option.station_code ?? '').toLowerCase();
    return name.includes(normalizedQuery) || code.includes(normalizedQuery);
  });
}

export function findReportOptionByLabel<T extends ReportOptionLike>(filteredOptions: T[], allOptions: T[], label: string): T | null {
  const normalizedLabel = normalizeReportSearchQuery(label);
  if (!normalizedLabel) {
    return null;
  }

  return (
    filteredOptions.find((option) => (option.name ?? '').trim().toLowerCase() === normalizedLabel) ??
    allOptions.find((option) => (option.name ?? '').trim().toLowerCase() === normalizedLabel) ??
    null
  );
}

export function clearNumericFormControlIfSelected(
  form: UntypedFormGroup,
  controlName: string,
  normalizeNumber: NormalizeNumberFn,
  emitEvent = true,
): void {
  if (normalizeNumber(form.get(controlName)?.value) !== null) {
    form.patchValue({ [controlName]: null }, { emitEvent });
  }
}

export function patchNumericFormControlIfChanged(
  form: UntypedFormGroup,
  controlName: string,
  value: number,
  normalizeNumber: NormalizeNumberFn,
  emitEvent = true,
): void {
  if (normalizeNumber(form.get(controlName)?.value) !== value) {
    form.patchValue({ [controlName]: value }, { emitEvent });
  }
}

export function handleReportAutocompleteSearch<TOption extends ReportOptionLike>(config: ReportAutocompleteSearchConfig<TOption>): void {
  const query = typeof config.query === 'string' ? config.query : '';
  config.filterOptions(query);

  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  const match = config.findOption(normalizedQuery);
  if (match?.id) {
    patchNumericFormControlIfChanged(config.form, config.controlName, Number(match.id), config.normalizeNumber);
  }
}

export function commitReportAutocompleteValue<TOption extends ReportOptionLike>(config: ReportAutocompleteCommitConfig<TOption>): void {
  const currentValue = typeof config.searchTerm === 'string' ? config.searchTerm.trim() : '';
  if (!currentValue) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  const match = config.findOption(currentValue);
  if (!match?.id) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  config.setSearchTerm(match.name ?? '');
  patchNumericFormControlIfChanged(config.form, config.controlName, Number(match.id), config.normalizeNumber);
}

export function stripTrailingParenthesizedText(value: string): string {
  if (!value.endsWith(')')) {
    return value;
  }

  const openingIndex = value.lastIndexOf('(');
  return openingIndex > 0 ? value.slice(0, openingIndex).trimEnd() : value;
}

export function stripTotalReportLabel(value: string | null | undefined): string {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return stripTrailingParenthesizedText(candidate).replace(/^total\s+/i, '').trim();
}

export function slugifyReportLabel(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function getReportHierarchyNodeOrderKey(row: ReportHierarchyOrderRowLike): string {
  if (row.row_kind === 'sector-total') {
    return `sector:${slugifyReportLabel(stripTotalReportLabel(row.station_name))}`;
  }

  return `row:${row.row_key ?? row.row_kind ?? ''}`;
}

export function buildReportBlockGroups<TBlock extends ReportBlockDefinitionLike, TPeriod extends string>(
  blocks: TBlock[],
  getPeriodTitle: (period: TPeriod) => string,
): ReportBlockGroupLike<TPeriod>[] {
  const groups: ReportBlockGroupLike<TPeriod>[] = [];

  for (const block of blocks) {
    const period = block.period as TPeriod;
    const lastGroup = groups[groups.length - 1] ?? null;
    if (lastGroup && lastGroup.period === period) {
      lastGroup.span += 1;
      continue;
    }

    groups.push({
      period,
      label: getPeriodTitle(period),
      span: 1,
    });
  }

  return groups;
}

export function downloadReportBlob(blob: Blob, fileName: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export function extractReportResponseValue(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const root = getReportRecord(response);
  if (Array.isArray(root['blocks']) || Array.isArray(root['rows'])) {
    return root;
  }

  const value = root['value'] ?? root['data'];
  return value && typeof value === 'object' ? getReportRecord(value) : null;
}

export function getReportArrayValue(value: unknown, key: string): unknown[] {
  const candidate = getReportRecordValue(value, key);
  return Array.isArray(candidate) ? candidate : [];
}

export function getReportRecordValue(value: unknown, key: string): unknown {
  const record = getReportRecord(value);
  return record[key] ?? null;
}

export function getReportRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return { ...(value as Record<string, unknown>) };
}

export function extractReportStringValue(value: unknown, key: string): string | null {
  const candidate = getReportRecordValue(value, key);
  if (typeof candidate !== 'string') {
    return null;
  }
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeReportNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function extractReportDateConversion(response: unknown): ReportDateConversionLike | null {
  const value = extractReportResponseValue(response);
  if (!value) {
    return null;
  }

  return {
    data_date: extractReportStringValue(value, 'data_date') ?? '',
    bs_date: extractReportStringValue(value, 'bs_date') ?? '',
    bs_date_label: extractReportStringValue(value, 'bs_date_label') ?? '',
    selected_date: extractReportStringValue(value, 'selected_date') ?? '',
    selected_date_label: extractReportStringValue(value, 'selected_date_label') ?? '',
  };
}

export function extractReportBsCalendar(response: unknown): ReportBsCalendarLike | null {
  const value = extractReportResponseValue(response);
  if (!value) {
    return null;
  }

  const cells = getReportArrayValue(value, 'cells').map((item) => {
    const record = getReportRecord(item);
    return {
      bs_date: extractReportStringValue(record, 'bs_date') ?? '',
      bs_date_label: extractReportStringValue(record, 'bs_date_label') ?? '',
      ad_date: extractReportStringValue(record, 'ad_date') ?? '',
      in_current_month: Boolean(record['in_current_month']),
      is_today: Boolean(record['is_today']),
      is_selected: Boolean(record['is_selected']),
    };
  });

  return {
    data_date: extractReportStringValue(value, 'data_date') ?? '',
    bs_date: extractReportStringValue(value, 'bs_date') ?? '',
    bs_year: normalizeReportNumber(value['bs_year']) ?? 0,
    bs_month: normalizeReportNumber(value['bs_month']) ?? 0,
    bs_month_name: extractReportStringValue(value, 'bs_month_name') ?? '',
    selected_date: extractReportStringValue(value, 'selected_date') ?? '',
    selected_date_label: extractReportStringValue(value, 'selected_date_label') ?? '',
    cells,
  };
}
